import React from 'react';
import { MdWifiOff } from 'react-icons/md';

import { NETWORK_OFFLINE_BANNER_MESSAGE } from 'constants/messageConstants';
import useNetworkStatus from 'hooks/useNetworkStatus';

/**
 * Global floating banner that slides down from the top of the viewport when
 * the browser detects a network outage. Mirrors the Google Docs offline strip.
 * Always present in the DOM (avoids mount/unmount flicker); hidden via
 * translateY when online so the slide-in transition plays smoothly.
 */
const OfflineBanner: React.FC = () => {
  const isOnline = useNetworkStatus();

  return (
    // Outer div handles positioning and slide animation only — no background.
    // The background is a separate absolute layer with opacity-50 so the text
    // and icon remain fully opaque (opacity is not inherited across stacking
    // contexts when sibling elements are used instead of parent/child).
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className={`fixed inset-x-0 top-0 z-[9000] transition-transform duration-300 ease-in-out${
        isOnline ? ' -translate-y-full' : ' translate-y-0'
      }`}
    >
      <div className="absolute inset-0 bg-information opacity-50" />
      <div className="relative flex items-center justify-center gap-2 py-2 px-4 text-sm font-semibold text-charcoal">
        <MdWifiOff size={16} aria-hidden="true" />
        <span>{NETWORK_OFFLINE_BANNER_MESSAGE}</span>
      </div>
    </div>
  );
};

export default OfflineBanner;
